import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlocker } from 'react-router-dom';
import { IScheduleReminder, IScheduleSurvey } from '../../services/surveys';
import { ConfirmSurveyQuitModal } from '../ConfirmSurveyQuitModal/ConfirmSurveyQuitModal';
import ContactPersonTile from './ContactPersonTile/ContactPersonTile';
import ScheduleContent from './ScheduleContent/ScheduleContent';
import ScheduleHeader from './ScheduleHeader/ScheduleHeader';
import { HeaderCell as Cell } from './ScheduleHeader/ScheduleHeader.styles';
import ScheduleReminder from './ScheduleReminder/ScheduleReminder';
import { AddReminderButton, Wrapper } from './ScheduleSurvey.styles';
import CirclePlus from '@/assets/icons/plusInCircle.svg?react';

interface ScheduleSurveyProps {
  externalSurveyId: string;
  schedule: IScheduleSurvey;
  updateSchedule: (updates: Partial<IScheduleSurvey>) => void;
  isTransitionBlocked: boolean;
  setIsTransitionBlocked: React.Dispatch<React.SetStateAction<boolean>>;
  onConfirmQuit: () => void;
}

const formatOptions = [
  { label: 'Email', disabled: true },
  { label: 'SMS', disabled: false },
];

const ScheduleSurvey: FC<ScheduleSurveyProps> = ({
  schedule,
  externalSurveyId,
  updateSchedule,
  isTransitionBlocked,
  setIsTransitionBlocked,
  onConfirmQuit,
}) => {
  const { t } = useTranslation();
  const { reminders } = schedule;

  const blocker = useBlocker(isTransitionBlocked);

  const isPassed =
    schedule.end_date && !isNaN(new Date(schedule.end_date).getTime())
      ? new Date(schedule.end_date) < new Date()
      : false;

  const handleCancelQuit = () => {
    blocker.reset?.();
  };

  const handleConfirmQuit = () => {
    setIsTransitionBlocked(false);
    onConfirmQuit();
    blocker.proceed?.();
  };

  const addReminder = () => {
    const newReminder: Partial<IScheduleReminder> = {
      execute_at: null,
      include_sms: false,
    };
    const updatedReminders = [...reminders, newReminder as IScheduleReminder];
    updateSchedule({
      ...schedule,
      reminders: updatedReminders,
    });
  };

  const updateReminder = (index: number, updatedFields: Partial<IScheduleReminder>) => {
    const updatedReminders = [...reminders];
    updatedReminders[index] = { ...updatedReminders[index], ...updatedFields };
    updateSchedule({
      ...schedule,
      reminders: updatedReminders,
    });
  };

  const deleteReminder = (index: number) => {
    const updatedReminders = reminders.filter((_, i) => i !== index);
    updateSchedule({
      ...schedule,
      reminders: updatedReminders,
    });
  };

  return (
    <Wrapper>
      <ScheduleHeader>
        <Cell>{t('viewSurvey.schedule.startDate')}</Cell>
        <Cell>{t('viewSurvey.schedule.date')}</Cell>
        <Cell>{t('viewSurvey.schedule.time')}</Cell>
        <Cell>{t('viewSurvey.schedule.format')}</Cell>
        <Cell>{t('viewSurvey.schedule.content')}</Cell>
      </ScheduleHeader>
      <ScheduleContent
        title={t('viewSurvey.schedule.surveyInitiation')}
        externalSurveyId={externalSurveyId}
        isSms={schedule.include_sms_invitation}
        setIsSms={() =>
          updateSchedule({ include_sms_invitation: !schedule.include_sms_invitation })
        }
        schedule={schedule}
        updateSchedule={updateSchedule}
        dateKey="start_date"
        notificationOptions={formatOptions}
      />
      <ScheduleHeader>
        <Cell></Cell>
        <Cell></Cell>
        <Cell></Cell>
      </ScheduleHeader>
      {reminders.map((reminder, index) => (
        <ScheduleReminder
          key={reminder.id ?? index}
          externalSurveyId={externalSurveyId}
          schedule={schedule}
          reminder={reminder}
          index={index}
          onUpdateReminder={updateReminder}
          onDeleteReminder={deleteReminder}
          isFormatOption={true}
          notificationOptions={formatOptions}
        />
      ))}

      {reminders.length < 10 && !isPassed && (
        <AddReminderButton onClick={addReminder}>
          <CirclePlus />
          {t('viewSurvey.schedule.addReminder')}
        </AddReminderButton>
      )}
      <ScheduleHeader>
        <Cell>{t('viewSurvey.schedule.endDate')}</Cell>
        <Cell>{t('viewSurvey.schedule.date')}</Cell>
        <Cell>{t('viewSurvey.schedule.time')}</Cell>
      </ScheduleHeader>
      <ScheduleContent
        title={t('viewSurvey.schedule.surveyWillClose')}
        externalSurveyId={externalSurveyId}
        schedule={schedule}
        isFormatOption={false}
        isModalOptions={false}
        updateSchedule={updateSchedule}
        dateKey="end_date"
        notificationOptions={formatOptions}
      />

      {externalSurveyId && (
        <ContactPersonTile schedule={schedule} updateSchedule={updateSchedule} />
      )}

      <ConfirmSurveyQuitModal
        onClose={handleCancelQuit}
        onConfirm={handleConfirmQuit}
        active={blocker.state === 'blocked'}
      />
    </Wrapper>
  );
};

export default ScheduleSurvey;
